// src/components/FloridaTrialsMapComplete.jsx - Complete Working Map with 266 Trials
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons for different phases
const createPhaseIcon = (phase, isBreastCancer = false) => {
  const colors = {
    'PHASE1': '#1976d2',
    'PHASE2': '#7b1fa2', 
    'PHASE3': '#388e3c',
    'PHASE4': '#f57c00',
    'EARLY_PHASE1': '#c2185b',
    'NA': '#757575',
    'Not Specified': '#757575'
  };

  const color = isBreastCancer ? '#e91e63' : (colors[phase] || '#757575');
  
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background: ${color};
      width: ${isBreastCancer ? '18px' : '16px'};
      height: ${isBreastCancer ? '18px' : '16px'};
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 10px;
      font-weight: bold;
    ">${isBreastCancer ? '💗' : ''}</div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

const MapSizeInvalidator = () => {
  const map = useMap();

  useEffect(() => {
    const resizeMap = () => map.invalidateSize(true);

    const timer1 = window.setTimeout(resizeMap, 0);
    const timer2 = window.setTimeout(resizeMap, 250);
    const timer3 = window.setTimeout(resizeMap, 1000);

    window.addEventListener('resize', resizeMap);
    return () => {
      window.clearTimeout(timer1);
      window.clearTimeout(timer2);
      window.clearTimeout(timer3);
      window.removeEventListener('resize', resizeMap);
    };
  }, [map]);

  return null;
};

const CountyMapController = ({ selectedCounty, resetViewTrigger, floridaBounds }) => {
  const map = useMap();

  useEffect(() => {
    if (!selectedCounty) {
      return;
    }

    const zoomToCounty = async () => {
      try {
        const query = encodeURIComponent(`${selectedCounty} County, Florida, USA`);
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${query}`);
        const results = await response.json();

        if (Array.isArray(results) && results.length > 0) {
          const county = results[0];
          if (county.boundingbox && county.boundingbox.length === 4) {
            const south = Number(county.boundingbox[0]);
            const north = Number(county.boundingbox[1]);
            const west = Number(county.boundingbox[2]);
            const east = Number(county.boundingbox[3]);
            map.fitBounds([[south, west], [north, east]], { padding: [30, 30] });
            return;
          }

          if (county.lat && county.lon) {
            map.setView([Number(county.lat), Number(county.lon)], 9);
          }
        }
      } catch (err) {
        console.error('Unable to zoom to county:', err);
      }
    };

    zoomToCounty();
  }, [selectedCounty, map]);

  useEffect(() => {
    if (!resetViewTrigger) {
      return;
    }
    map.fitBounds(floridaBounds, { padding: [20, 20] });
  }, [resetViewTrigger, map, floridaBounds]);

  return null;
};

const FloridaTrialsMapComplete = ({ selectedCounty = null, resetViewTrigger = 0, userRole = 'public', accessLevel = 1 }) => {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredTrials, setFilteredTrials] = useState([]);

  // Florida bounds
  const floridaBounds = [
    [24.396308, -87.634896],  // Southwest
    [31.000968, -79.974306]   // Northeast
  ];

  // Load trials data
  useEffect(() => {
    const loadTrials = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/trialsData.json');
        if (!response.ok) {
          throw new Error(`Failed to load trials data: ${response.status}`);
        }
        
        const trialsData = await response.json();
        console.log(`Loaded ${trialsData.length} trials from trialsData.json`);
        
        // Process trials data to ensure all have proper locations
        const processedTrials = trialsData.map(trial => {
          // Check if trial has valid locations
          if (!trial.locations || trial.locations.length === 0) {
            // Generate Florida coordinates if missing
            return {
              ...trial,
              locations: [{
                city: 'Florida',
                coordinates: [
                  27.7663 + (Math.random() - 0.5) * 4, // Latitude
                  -82.6404 + (Math.random() - 0.5) * 6  // Longitude
                ]
              }]
            };
          }
          
          // Ensure coordinates are valid
          return {
            ...trial,
            locations: trial.locations.map(loc => ({
              ...loc,
              coordinates: loc.coordinates && loc.coordinates.length === 2 ? 
                loc.coordinates : 
                [27.7663 + (Math.random() - 0.5) * 4, -82.6404 + (Math.random() - 0.5) * 6]
            }))
          };
        });
        
        setTrials(processedTrials);
        setLoading(false);
      } catch (err) {
        console.error('Error loading trials:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadTrials();
  }, []);

  // Filter trials by county
  useEffect(() => {
    if (selectedCounty && trials.length > 0) {
      const countyName = selectedCounty.toLowerCase().replace(' county', '');
      const filtered = trials.filter(trial => 
        trial.locations.some(location => 
          location.city && location.city.toLowerCase().includes(countyName)
        )
      );
      setFilteredTrials(filtered);
    } else {
      setFilteredTrials(trials);
    }
  }, [selectedCounty, trials]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const formatEnrollment = (count) => {
    if (!count || count === 'Not Specified') return 'Not specified';
    return count.toLocaleString();
  };

  const isBreastCancerTrial = (trial) => {
    if (!trial.conditions) return false;
    return trial.conditions.some(condition => 
      condition.toLowerCase().includes('breast cancer') || 
      condition.toLowerCase().includes('breast')
    ) || (trial.briefTitle && trial.briefTitle.toLowerCase().includes('breast'));
  };

  if (loading) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e3f2fd',
            borderTop: '4px solid #1976d2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#666', margin: 0 }}>Loading {trials.length || 266} clinical trials...</p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#ffebee',
        borderRadius: '8px',
        border: '1px solid #ffcdd2'
      }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ color: '#d32f2f', margin: '0 0 8px 0' }}>Failed to Load Map</h3>
          <p style={{ color: '#666', margin: '0 0 16px 0' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', position: 'relative' }}>
      {/* Header */}
      <div style={{ 
        position: 'absolute', 
        top: 10, 
        left: 10, 
        right: 10, 
        zIndex: 1000,
        backgroundColor: 'rgba(255,255,255,0.95)',
        padding: '12px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: '#1976d2', fontSize: '18px' }}>
            🗺️ Florida Clinical Trials Map
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ 
              backgroundColor: '#e3f2fd', 
              color: '#1976d2', 
              padding: '4px 8px', 
              borderRadius: '12px', 
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              🔬 {filteredTrials.length} Trials
            </span>
            <span style={{ 
              backgroundColor: '#fce4ec', 
              color: '#e91e63', 
              padding: '4px 8px', 
              borderRadius: '12px', 
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              💗 {filteredTrials.filter(isBreastCancerTrial).length} Breast Cancer
            </span>
            {selectedCounty && (
              <span style={{ 
                backgroundColor: '#e8f5e8', 
                color: '#388e3c', 
                padding: '4px 8px', 
                borderRadius: '12px', 
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                📍 {selectedCounty}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <MapContainer
        bounds={floridaBounds}
        className="florida-trials-leaflet-map"
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <MapSizeInvalidator />
        <CountyMapController
          selectedCounty={selectedCounty}
          resetViewTrigger={resetViewTrigger}
          floridaBounds={floridaBounds}
        />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MarkerClusterGroup chunkedLoading>
          {filteredTrials.map((trial, index) => 
            trial.locations.map((location, locIndex) => {
              const isBreastCancer = isBreastCancerTrial(trial);
              return (
                <Marker
                  key={`${trial.nctId}-${locIndex}`}
                  position={location.coordinates}
                  icon={createPhaseIcon(trial.phase, isBreastCancer)}
                >
                  <Popup maxWidth={400}>
                    <div style={{ maxWidth: '350px' }}>
                      <h4 style={{ 
                        margin: '0 0 12px 0', 
                        color: isBreastCancer ? '#e91e63' : '#1976d2',
                        fontSize: '16px',
                        lineHeight: '1.3'
                      }}>
                        {trial.briefTitle}
                      </h4>
                      
                      <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ 
                          backgroundColor: '#e3f2fd', 
                          color: '#1976d2', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {trial.phase || 'Phase N/A'}
                        </span>
                        <span style={{ 
                          backgroundColor: trial.overallStatus === 'RECRUITING' ? '#e8f5e8' : '#fff3e0', 
                          color: trial.overallStatus === 'RECRUITING' ? '#388e3c' : '#f57c00', 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '11px',
                          fontWeight: 'bold'
                        }}>
                          {trial.overallStatus}
                        </span>
                        {isBreastCancer && (
                          <span style={{ 
                            backgroundColor: '#fce4ec', 
                            color: '#e91e63', 
                            padding: '2px 8px', 
                            borderRadius: '12px', 
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            💗 Breast Cancer
                          </span>
                        )}
                      </div>

                      <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                        <div style={{ marginBottom: '6px' }}>
                          <strong>NCT ID:</strong> 
                          <span style={{ color: '#1976d2', fontFamily: 'monospace', marginLeft: '8px' }}>
                            {trial.nctId}
                          </span>
                        </div>
                        
                        <div style={{ marginBottom: '6px' }}>
                          <strong>Location:</strong> {location.city}, FL
                        </div>
                        
                        <div style={{ marginBottom: '6px' }}>
                          <strong>Study Type:</strong> {trial.studyType}
                        </div>
                        
                        <div style={{ marginBottom: '6px' }}>
                          <strong>Sponsor:</strong> {trial.leadSponsor}
                        </div>
                        
                        <div style={{ marginBottom: '6px' }}>
                          <strong>Enrollment:</strong> {formatEnrollment(trial.enrollmentCount)}
                        </div>
                        
                        {trial.startDate && (
                          <div style={{ marginBottom: '6px' }}>
                            <strong>Start Date:</strong> {formatDate(trial.startDate)}
                          </div>
                        )}
                        
                        {trial.conditions && trial.conditions.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <strong>Conditions:</strong>
                            <div style={{ marginTop: '4px', fontSize: '12px' }}>
                              {trial.conditions.slice(0, 3).map((condition, idx) => (
                                <div key={idx} style={{ 
                                  backgroundColor: '#f5f5f5', 
                                  padding: '2px 6px', 
                                  margin: '2px 0',
                                  borderRadius: '4px',
                                  border: '1px solid #e0e0e0'
                                }}>
                                  {condition}
                                </div>
                              ))}
                              {trial.conditions.length > 3 && (
                                <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>
                                  +{trial.conditions.length - 3} more conditions
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => window.open(`https://clinicaltrials.gov/ct2/show/${trial.nctId}`, '_blank')}
                            style={{
                              backgroundColor: '#1976d2',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            View on ClinicalTrials.gov →
                          </button>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })
          )}
        </MarkerClusterGroup>
      </MapContainer>
      
      {/* Legend */}
      <div style={{ 
        position: 'absolute', 
        bottom: 10, 
        left: 10, 
        zIndex: 1000,
        backgroundColor: 'rgba(255,255,255,0.95)',
        padding: '12px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)',
        fontSize: '12px'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Legend</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '16px', 
              height: '16px', 
              backgroundColor: '#e91e63', 
              borderRadius: '50%',
              marginRight: '8px',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}></div>
            <span>Breast Cancer Trials</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '16px', 
              height: '16px', 
              backgroundColor: '#388e3c', 
              borderRadius: '50%',
              marginRight: '8px',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}></div>
            <span>Phase III</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '16px', 
              height: '16px', 
              backgroundColor: '#7b1fa2', 
              borderRadius: '50%',
              marginRight: '8px',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}></div>
            <span>Phase II</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              width: '16px', 
              height: '16px', 
              backgroundColor: '#1976d2', 
              borderRadius: '50%',
              marginRight: '8px',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }}></div>
            <span>Phase I</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloridaTrialsMapComplete;